import { useCallback, useEffect, useState } from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { useDispatch } from 'react-redux';

import { api } from '@/app/api';
import { showNotificationPush } from '@shared/slices/notificationPushSlice';
import { UiButton, UiInput, UiNativeSelect } from '@shared/common/ui';

const EMPTY_FORM = { name: '', domain: '', mailSender: 'mod' };

function errorMessage(error) {
  return error?.response?.data?.message ?? error?.message ?? 'Не удалось выполнить запрос';
}

function formatDate(value) {
  if (!value) return 'Ещё не использовался';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString('ru-RU');
}

function CopyIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  );
}

export function ApiKeysSettings() {
  const dispatch = useDispatch();
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [status, setStatus] = useState('loading');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setStatus('loading');
    setError('');
    try {
      const { data } = await api.get('/owner/api-keys');
      setItems(data.items ?? []);
      setStatus('success');
    } catch (requestError) {
      setError(errorMessage(requestError));
      setStatus('error');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const closeDrawer = () => {
    if (saving) return;
    setDrawerOpen(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
    setError('');
  };

  const openNew = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setError('');
    setDrawerOpen(true);
  };

  const openEdit = (item) => {
    setEditingId(item.id);
    setForm({
      name: item.name,
      domain: item.domain ?? '',
      mailSender: item.mailSender ?? 'mod',
    });
    setError('');
    setDrawerOpen(true);
  };

  const submit = async (event) => {
    event.preventDefault();
    const payload = {
      name: form.name.trim(),
      domain: form.domain.trim() || null,
      mailSender: form.mailSender,
    };
    if (!payload.name) {
      setError('Укажите название ключа');
      return;
    }
    setSaving(true);
    setError('');
    try {
      if (editingId) {
        const previous = items.find((item) => item.id === editingId);
        const { data } = await api.patch(`/owner/api-keys/${editingId}`, payload);
        setItems((current) => current.map((item) => (
          item.id === editingId ? { ...data.item, apiKey: previous?.apiKey } : item
        )));
      } else {
        const { data } = await api.post('/owner/api-keys', payload);
        setItems((current) => [{ ...data.item, apiKey: data.apiKey }, ...current]);
        dispatch(showNotificationPush({ content: 'API-ключ создан' }));
      }
      setDrawerOpen(false);
      setEditingId(null);
      setForm(EMPTY_FORM);
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (item) => {
    setError('');
    try {
      const { data } = await api.patch(`/owner/api-keys/${item.id}`, { active: !item.active });
      setItems((current) => current.map((currentItem) => (
        currentItem.id === item.id ? { ...data.item, apiKey: currentItem.apiKey } : currentItem
      )));
    } catch (requestError) {
      setError(errorMessage(requestError));
    }
  };

  const rotate = async (item) => {
    if (!window.confirm(`Перевыпустить ключ «${item.name}»? Старый ключ сразу перестанет работать.`)) return;
    setError('');
    try {
      const { data } = await api.post(`/owner/api-keys/${item.id}/rotate`);
      setItems((current) => current.map((currentItem) => (
        currentItem.id === item.id ? { ...data.item, apiKey: data.apiKey } : currentItem
      )));
      dispatch(showNotificationPush({ content: 'API-ключ перевыпущен' }));
    } catch (requestError) {
      setError(errorMessage(requestError));
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Удалить API-ключ «${item.name}»? Это действие нельзя отменить.`)) return;
    setError('');
    try {
      await api.delete(`/owner/api-keys/${item.id}`);
      setItems((current) => current.filter((currentItem) => currentItem.id !== item.id));
    } catch (requestError) {
      setError(errorMessage(requestError));
    }
  };

  return (
    <div className="api-keys-settings">
      <div className="page-header page-header--spread">
        <div>
          <h2>API-ключи</h2>
          <p className="muted" style={{ margin: '6px 0 0' }}>
            Ключ используется сервером сайта или приложения в заголовке <code>x-api-key</code>.
          </p>
        </div>
        <UiButton type="button" onClick={openNew} title="Новый API-ключ" aria-label="Новый API-ключ">
          +
        </UiButton>
      </div>

      {error && !drawerOpen && <div className="banner banner--danger">{error}</div>}

      <div className="api-keys-settings__table-wrap">
        {status === 'loading' ? (
          <div className="placeholder">Загрузка…</div>
        ) : items.length === 0 ? (
          <div className="placeholder">API-ключи ещё не созданы</div>
        ) : (
          <table className="data-table api-keys-settings__table">
            <thead>
              <tr>
                <th>Название</th>
                <th>Ключ</th>
                <th>Домен</th>
                <th>Отправитель</th>
                <th>Последнее использование</th>
                <th>Статус</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id}>
                  <td>{item.name}</td>
                  <td>
                    <div className="api-keys-settings__key-cell">
                      <code>{item.keyPreview}</code>
                      <CopyToClipboard
                        text={item.apiKey ?? ''}
                        onCopy={() => dispatch(showNotificationPush({ content: 'API-ключ скопирован!' }))}
                      >
                        <button
                          type="button"
                          className="api-keys-settings__copy"
                          title="Скопировать API-ключ"
                          aria-label={`Скопировать API-ключ ${item.name}`}
                          disabled={!item.apiKey}
                        >
                          <CopyIcon />
                        </button>
                      </CopyToClipboard>
                    </div>
                  </td>
                  <td>{item.domain || '—'}</td>
                  <td>{item.mailSender}</td>
                  <td>{formatDate(item.lastUsedAt)}</td>
                  <td>{item.active ? 'Активен' : 'Отключён'}</td>
                  <td>
                    <div className="row-actions">
                      <UiButton type="button" size="sm" variant="ghost" onClick={() => openEdit(item)}>
                        Изменить
                      </UiButton>
                      <UiButton type="button" size="sm" variant="ghost" onClick={() => toggle(item)}>
                        {item.active ? 'Отключить' : 'Включить'}
                      </UiButton>
                      <UiButton type="button" size="sm" variant="ghost" onClick={() => rotate(item)}>
                        Перевыпустить
                      </UiButton>
                      <UiButton type="button" size="sm" variant="danger" onClick={() => remove(item)}>
                        Удалить
                      </UiButton>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {drawerOpen && (
        <div className="drawer-backdrop" onClick={closeDrawer}>
          <div className="drawer api-keys-settings__drawer" onClick={(event) => event.stopPropagation()}>
            <header className="drawer__header">
              <h2>{editingId ? 'Настройки API-ключа' : 'Новый API-ключ'}</h2>
              <UiButton type="button" variant="ghost" onClick={closeDrawer} disabled={saving}>
                Закрыть
              </UiButton>
            </header>

            <form onSubmit={submit}>
              <div className="form-grid">
                <UiInput
                  label="Название"
                  value={form.name}
                  maxLength={120}
                  placeholder="Например, сайт MOD"
                  autoFocus
                  onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
                />
                <UiInput
                  label="Домен сайта (необязательно)"
                  value={form.domain}
                  maxLength={500}
                  placeholder="https://mod-dance.ru"
                  onChange={(event) => setForm((current) => ({ ...current, domain: event.target.value }))}
                />
                <UiNativeSelect
                  label="Отправитель рассылки"
                  value={form.mailSender}
                  onChange={(event) => setForm((current) => ({ ...current, mailSender: event.target.value }))}
                >
                  <option value="mod">mod</option>
                </UiNativeSelect>
              </div>

              {error && <div className="banner banner--danger">{error}</div>}

              <div className="form-actions">
                <UiButton type="button" variant="ghost" onClick={closeDrawer} disabled={saving}>
                  Отмена
                </UiButton>
                <UiButton type="submit" disabled={saving}>
                  {saving ? 'Сохранение…' : editingId ? 'Сохранить' : 'Создать ключ'}
                </UiButton>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
